import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import PodValidationModal from './PodValidationModal';

/**
 * Bandeau d'action POD pour le client (Phase 3b — PM Spec 07/05/2026)
 *
 * Visible uniquement quand :
 *   - Un bon de déchargement signé existe (≥2 signatures)  ← géré côté backend (validation 400 sinon)
 *   - clientValidation absent (pas encore validé/disputé)
 *
 * Si déjà validé/disputé → état figé (badge informatif)
 *
 * Props:
 *   targetKind: 'operation' | 'transfer'
 *   operationId | (contractId + runId)
 *   label: string (numéro affiché)
 *   clientValidation?: { status, validatedAt, dispute? }
 *   hasSignedUnloadingNote: bool
 *   onChange?: () => void  // refresh callback après action
 */
const PodValidationBanner = ({
  targetKind,
  operationId,
  contractId,
  runId,
  label,
  clientValidation,
  hasSignedUnloadingNote = true,
  onChange,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('validate');

  // État déjà figé
  if (clientValidation?.status === 'validated') {
    return (
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-emerald-900">Livraison validée par le client</p>
          {clientValidation.validatedAt && (
            <p className="text-xs text-emerald-700">
              Le {new Date(clientValidation.validatedAt).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (clientValidation?.status === 'disputed') {
    return (
      <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-900">Litige signalé — en cours de traitement</p>
          {clientValidation?.dispute?.comment && (
            <p className="text-xs text-amber-700 truncate">
              {clientValidation.dispute.comment}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Bandeau actif d'action
  if (!hasSignedUnloadingNote) {
    return null; // discret : ne s'affiche que si bon signé
  }

  return (
    <>
      <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start gap-3 mb-3">
          <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-900">
              Confirmez la bonne réception de votre livraison
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              Le bon de déchargement a été signé. Validez pour clore l'opération ou signalez un litige si nécessaire.
            </p>
          </div>
          <Badge variant="outline" className="text-[10px] bg-white">{label}</Badge>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => { setModalMode('validate'); setModalOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="pod-banner-validate-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            Valider la livraison
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setModalMode('dispute'); setModalOpen(true); }}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="pod-banner-dispute-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-1.5" />
            Signaler un litige
          </Button>
        </div>
      </div>

      <PodValidationModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={modalMode}
        targetKind={targetKind}
        operationId={operationId}
        contractId={contractId}
        runId={runId}
        label={label}
        onSuccess={onChange}
      />
    </>
  );
};

export default PodValidationBanner;
